import React, { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { motion } from 'framer-motion';
import { Layers, Plus, Trash2, Droplets, Info } from 'lucide-react';
import { getCommonCurrencies, getAllCurrencyNames } from '../../utils/currencyLookup';

const commonCurrencies = getCommonCurrencies();
const allCurrencyNames = getAllCurrencyNames();

const GatewayConverterNode = ({ id, data }) => {
  const { 
    onUpdate,
    name = 'Bridge',
    currencies = [{ currency: 'VRSC', weight: 0.5 }],
    initialsupply = 100000,
    gatewayconverterissuance = 0,
    prelaunchcarveout = 0,
    idregistrationfees = 100,
    idreferrallevels = 3,
  } = data;

  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleAddCurrency = () => {
    const newCurrencies = [...currencies, { currency: '', weight: 0 }];
    onUpdate?.({ currencies: newCurrencies });
  };

  const handleRemoveCurrency = (index) => {
    if (currencies.length > 1) {
      const newCurrencies = currencies.filter((_, i) => i !== index);
      onUpdate?.({ currencies: newCurrencies });
    }
  };

  const handleCurrencyChange = (index, field, value) => {
    const newCurrencies = [...currencies];
    newCurrencies[index] = { ...newCurrencies[index], [field]: value };
    onUpdate?.({ currencies: newCurrencies });
  };

  const totalWeight = currencies.reduce((sum, c) => sum + (parseFloat(c.weight) || 0), 0);
  const isValidWeight = Math.abs(totalWeight - 1) < 0.001;

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="bg-slate-800 rounded-xl border-2 border-emerald-500/50 shadow-xl min-w-[340px] max-w-[380px]"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-black-600 rounded-t-lg px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
            <Layers className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm">Gateway Converter</h3>
            <p className="text-white/70 text-xs">Bridge liquidity pool configuration</p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-4">
        {/* Info Banner */}
        <div className="flex items-start gap-2 p-2 bg-orange-500/10 border border-orange-500/30 rounded-lg">
          <Info className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
          <p className="text-[10px] text-orange-300">
            PBaaS chains launch with a Bridge converter - a fractional currency (AMM) 
            that enables cross-chain conversions between your chain and reserve currencies.
          </p>
        </div>

        {/* Bridge Name */}
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1.5">
            Bridge Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => onUpdate?.({ name: e.target.value })}
            placeholder="Bridge"
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
          />
        </div>

        {/* Reserve Currencies */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-slate-400">
              Reserve Currencies
            </label>
            <button
              onClick={handleAddCurrency}
              className="flex items-center gap-1 text-xs px-2 py-1 bg-white text-slate-800 font-medium rounded hover:bg-slate-100 transition-colors shadow-sm"
            >
              <Plus className="w-3 h-3" />
              Add
            </button>
          </div>
          
          <div className="space-y-2">
            {currencies.map((curr, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="flex-1">
                  <select
                    value={curr.currency}
                    onChange={(e) => handleCurrencyChange(index, 'currency', e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  >
                    <option value="">Select currency</option>
                    <option value="[CHAIN]">[Your Chain] (auto)</option>
                    {commonCurrencies.map((currName) => (
                      <option key={currName} value={currName}>{currName}</option>
                    ))}
                    <optgroup label="All Currencies">
                      {allCurrencyNames.filter(name => !commonCurrencies.includes(name)).map((currName) => (
                        <option key={currName} value={currName}>{currName}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>
                <div className="w-20">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={curr.weight}
                    onChange={(e) => handleCurrencyChange(index, 'weight', parseFloat(e.target.value) || 0)}
                    placeholder="Weight"
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  />
                </div>
                {currencies.length > 1 && (
                  <button
                    onClick={() => handleRemoveCurrency(index)}
                    className="p-1 text-slate-500 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
          
          {/* Weight validation */}
          <div className={`mt-2 text-xs flex items-center justify-between ${isValidWeight ? 'text-emerald-400' : 'text-amber-400'}`}>
            <span>Total weight: {totalWeight.toFixed(2)}</span>
            {!isValidWeight && <span>Must equal 1.0</span>}
          </div>
        </div>

        {/* Initial Supply & Issuance */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">
              Initial Supply
            </label>
            <input
              type="number"
              value={initialsupply}
              onChange={(e) => onUpdate?.({ initialsupply: parseFloat(e.target.value) || 0 })}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">
              Gateway Issuance
            </label>
            <input
              type="number"
              value={gatewayconverterissuance}
              onChange={(e) => onUpdate?.({ gatewayconverterissuance: parseFloat(e.target.value) || 0 })}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            />
            <p className="text-[10px] text-slate-500 mt-0.5">Native coins for bridge</p>
          </div>
        </div>

        {/* Advanced Toggle */}
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full text-xs text-slate-400 hover:text-slate-300 transition-colors py-1"
        >
          {showAdvanced ? '▼ Hide Advanced' : '▶ Show Advanced'}
        </button>

        {/* Advanced Options */}
        {showAdvanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="space-y-3 pt-2 border-t border-slate-700/50"
          >
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">
                Prelaunch Carveout (0-1)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={prelaunchcarveout}
                onChange={(e) => onUpdate?.({ prelaunchcarveout: parseFloat(e.target.value) || 0 })}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
              />
              <p className="text-[10px] text-slate-500 mt-0.5">Portion reserved for launch controller</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">
                  ID Reg. Fees
                </label>
                <input
                  type="number"
                  value={idregistrationfees}
                  onChange={(e) => onUpdate?.({ idregistrationfees: parseFloat(e.target.value) || 0 })}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">
                  Referral Levels
                </label>
                <input
                  type="number"
                  min="0"
                  max="3"
                  value={idreferrallevels}
                  onChange={(e) => onUpdate?.({ idreferrallevels: parseInt(e.target.value) || 0 })}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                />
              </div>
            </div>
          </motion.div>
        )}

        {/* Visual representation */}
        <div className="flex items-center justify-center gap-2 py-2 border-t border-slate-700/50">
          {currencies.slice(0, 3).map((curr, index) => (
            <React.Fragment key={index}>
              {index > 0 && (
                <div className="text-emerald-400 text-xs">⟷</div>
              )}
              <div className="flex flex-col items-center gap-1">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  index === 0 ? 'bg-blue-500/20 border border-blue-500/50' : 
                  index === 1 ? 'bg-purple-500/20 border border-purple-500/50' :
                  'bg-amber-500/20 border border-amber-500/50'
                }`}>
                  <Droplets className={`w-4 h-4 ${
                    index === 0 ? 'text-blue-400' : 
                    index === 1 ? 'text-purple-400' :
                    'text-amber-400'
                  }`} />
                </div>
                <span className="text-[9px] text-slate-500 truncate max-w-[60px]">
                  {curr.currency === '[CHAIN]' ? 'Chain' : curr.currency || '?'}
                </span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3 !h-3 !bg-emerald-500 !border-2 !border-slate-800"
      />
    </motion.div>
  );
};

export default GatewayConverterNode;
